<template>
  <CDropdown
    ref="dropdownRef"
    placement="bottom-end"
    class="c-header-nav-item app-switcher"
    :visible="isOpen"
    @hide="onDropdownHide"
    @show="onDropdownShow"
  >
    <CDropdownToggle
      ref="toggleRef"
      component="button"
      color="link"
      class="header-actions-icon header-focus-ring-trigger app-switcher__toggle p-0 border-0 d-inline-flex align-items-center justify-content-center shadow-none"
      aria-label="Open app switcher"
      aria-haspopup="menu"
      :aria-expanded="isOpen ? 'true' : 'false'"
      aria-controls="app-switcher-menu"
      data-test-id="header-app-switcher-btn"
      @click.prevent="toggleMenu"
      @keydown="onToggleKeydown"
    >
      <img
        class="app-switcher__trigger-icon"
        src="/img/app-switcher/app-switcher-trigger.svg"
        alt=""
        width="24"
        height="24"
        aria-hidden="true"
      />
    </CDropdownToggle>

    <CDropdownMenu
      id="app-switcher-menu"
      ref="menuRef"
      class="app-switcher__menu"
      role="menu"
      aria-labelledby="app-switcher-menu-label"
      @keydown="onMenuKeydown"
    >
      <div class="app-switcher__header" role="presentation">
        <span id="app-switcher-menu-label" class="app-switcher__header-label">
          Go to...
        </span>
      </div>

      <CDropdownItem
        v-for="app in apps"
        :key="app.id"
        class="app-switcher__item-wrap p-0"
        :href="app.loginUrl"
        target="_blank"
        rel="noopener noreferrer"
        role="menuitem"
        :aria-label="`${app.name} (opens in a new tab)`"
        :data-test-id="`app-switcher-item-${app.id}`"
        @click="closeMenu"
      >
        <span class="app-switcher__item">
          <span class="app-switcher__icon" aria-hidden="true">
            <img :src="app.iconSrc" alt="" width="15" height="15" />
          </span>
          <span class="app-switcher__name" aria-hidden="true">
            {{ app.name }}
          </span>
        </span>
      </CDropdownItem>
    </CDropdownMenu>
  </CDropdown>
</template>

<script>
import { CURRENT_APP_ID, getSwitchableApps } from "@/constants/appSwitcher"
import { computed, nextTick, onBeforeUnmount, ref } from "vue"

const MENU_ITEM_SELECTOR =
  '#app-switcher-menu [role="menuitem"]:not([disabled]):not([aria-disabled="true"])'

export default {
  name: "AppSwitcher",
  setup() {
    const isOpen = ref(false)
    const dropdownRef = ref(null)
    const toggleRef = ref(null)
    const menuRef = ref(null)
    let trapBound = false

    const apps = computed(() => getSwitchableApps(CURRENT_APP_ID))

    const getToggleEl = () => {
      const toggleVm = toggleRef.value
      if (!toggleVm) return null
      // CoreUI may expose DOM via $el or the ref may already be the element
      if (toggleVm instanceof HTMLElement) return toggleVm
      if (toggleVm.$el instanceof HTMLElement) return toggleVm.$el
      return (
        document.querySelector('[data-test-id="header-app-switcher-btn"]') ||
        null
      )
    }

    const getMenuEl = () => {
      return document.getElementById("app-switcher-menu")
    }

    const getMenuItems = () => {
      const menu = getMenuEl()
      if (!menu) return []
      return Array.from(menu.querySelectorAll(MENU_ITEM_SELECTOR)).filter(
        (el) =>
          el.getAttribute("aria-hidden") !== "true" &&
          !el.closest?.("[aria-hidden='true']") &&
          !el.closest?.("[inert]")
      )
    }

    const focusMenuItem = (index) => {
      const items = getMenuItems()
      if (!items.length) return
      const safeIndex = ((index % items.length) + items.length) % items.length
      items[safeIndex]?.focus?.({ preventScroll: true })
    }

    const focusToggle = () => {
      const toggle = getToggleEl()
      if (toggle && typeof toggle.focus === "function") {
        toggle.focus({ preventScroll: true })
        return
      }
      document
        .querySelector('[data-test-id="header-app-switcher-btn"]')
        ?.focus?.({ preventScroll: true })
    }

    const openMenu = () => {
      isOpen.value = true
    }

    const closeMenu = () => {
      if (!isOpen.value) return
      isOpen.value = false
      nextTick(() => focusToggle())
    }

    const toggleMenu = () => {
      if (isOpen.value) {
        closeMenu()
      } else {
        openMenu()
      }
    }

    const onToggleKeydown = (event) => {
      if (
        event.key === "ArrowDown" ||
        event.key === "Enter" ||
        event.key === " "
      ) {
        if (!isOpen.value) {
          event.preventDefault()
          openMenu()
        } else if (event.key === "ArrowDown") {
          event.preventDefault()
          focusMenuItem(0)
        }
      } else if (event.key === "Escape" && isOpen.value) {
        event.preventDefault()
        event.stopPropagation()
        closeMenu()
      } else if (event.key === "Tab" && isOpen.value) {
        // Move into menu without a default selected look until keyboard focus
        event.preventDefault()
        const items = getMenuItems()
        if (!items.length) return
        focusMenuItem(event.shiftKey ? items.length - 1 : 0)
      }
    }

    const onMenuKeydown = (event) => {
      if (!isOpen.value) return

      const items = getMenuItems()
      if (!items.length) return

      const activeIndex = items.indexOf(document.activeElement)

      switch (event.key) {
        case "Escape":
          event.preventDefault()
          event.stopPropagation()
          closeMenu()
          break
        case "ArrowDown":
          event.preventDefault()
          focusMenuItem(activeIndex < 0 ? 0 : activeIndex + 1)
          break
        case "ArrowUp":
          event.preventDefault()
          focusMenuItem(activeIndex < 0 ? items.length - 1 : activeIndex - 1)
          break
        case "Home":
          event.preventDefault()
          focusMenuItem(0)
          break
        case "End":
          event.preventDefault()
          focusMenuItem(items.length - 1)
          break
        case "Tab": {
          // Focus loop: menuitems ↔ toggle (no default selected item)
          event.preventDefault()
          if (event.shiftKey) {
            if (activeIndex <= 0) {
              focusToggle()
            } else {
              focusMenuItem(activeIndex - 1)
            }
          } else if (activeIndex < 0 || activeIndex >= items.length - 1) {
            focusToggle()
          } else {
            focusMenuItem(activeIndex + 1)
          }
          break
        }
        default:
          break
      }
    }

    const onDocumentKeydown = (event) => {
      if (!isOpen.value) return
      if (event.key === "Escape") {
        event.preventDefault()
        event.stopPropagation()
        closeMenu()
      }
    }

    const onDocumentFocusIn = (event) => {
      if (!isOpen.value || !trapBound) return
      const menu = getMenuEl()
      const toggle = getToggleEl()
      const target = event.target
      if (!menu || !target) return
      if (menu.contains(target)) return
      if (toggle && (toggle === target || toggle.contains?.(target))) return
      // Keep focus on the toggle so no menuitem appears selected by default
      event.preventDefault?.()
      focusToggle()
    }

    const bindTrap = () => {
      if (trapBound) return
      document.addEventListener("keydown", onDocumentKeydown, true)
      document.addEventListener("focusin", onDocumentFocusIn, true)
      trapBound = true
    }

    const unbindTrap = () => {
      if (!trapBound) return
      document.removeEventListener("keydown", onDocumentKeydown, true)
      document.removeEventListener("focusin", onDocumentFocusIn, true)
      trapBound = false
    }

    const onDropdownShow = () => {
      isOpen.value = true
      bindTrap()
      // Keep focus on the toggle — no menuitem selected/highlighted by default
      nextTick(() => {
        requestAnimationFrame(() => {
          focusToggle()
        })
      })
    }

    const onDropdownHide = () => {
      const wasOpen = isOpen.value
      isOpen.value = false
      unbindTrap()
      if (wasOpen) {
        nextTick(() => focusToggle())
      }
    }

    onBeforeUnmount(() => {
      unbindTrap()
    })

    return {
      apps,
      isOpen,
      dropdownRef,
      toggleRef,
      menuRef,
      toggleMenu,
      closeMenu,
      onToggleKeydown,
      onMenuKeydown,
      onDropdownShow,
      onDropdownHide
    }
  }
}
</script>
